<script setup>
import { onMounted, nextTick } from 'vue'
import { ScrollSpy } from 'bootstrap'
import PageToolbar from '@/components/common/PageToolbar.vue'
import ToolbarHeading from '@/components/common/ToolbarHeading.vue'

// Getting started
import Introduction from '@product/views/Docs/Api/Sections/Introduction.vue'
import Authentication from '@product/views/Docs/Api/Sections/Authentication.vue'
import ResponseTypes from '@product/views/Docs/Api/Sections/ResponseTypes.vue'
import FairUse from '@product/views/Docs/Api/Sections/FairUse.vue'

// IP intelligence
import IpInfo from '@product/views/Docs/Api/Sections/IpInfo.vue'
import IpBulk from '@product/views/Docs/Api/Sections/IpBulk.vue'
import IpSchema from '@product/views/Docs/Api/Sections/IpSchema.vue'

// Event search
import EventSearch from '@product/views/Docs/Api/Sections/EventSearch.vue'
import EventAggregate from '@product/views/Docs/Api/Sections/EventAggregate.vue'
import ExportSearch from '@product/views/Docs/Api/Sections/ExportSearch.vue'
import ExportAggregate from '@product/views/Docs/Api/Sections/ExportAggregate.vue'

// Discovery
import Sources from '@product/views/Docs/Api/Sections/Sources.vue'
import SourcesSchema from '@product/views/Docs/Api/Sections/SourcesSchema.vue'

// Datasets
import EnrichmentDownload from '@product/views/Docs/Api/Sections/EnrichmentDownload.vue'

// TOC manifest, anchors are the shared contract between each section's
// :anchor prop and its nav link. ScrollSpy highlights the active entry.
const groups = [
  {
    label: 'Getting started',
    items: [
      { id: 'introduction', label: 'Introduction' },
      { id: 'authentication', label: 'Authentication' },
      { id: 'response-format', label: 'Response format' },
      { id: 'rate-limits', label: 'Rate limits' },
    ],
  },
  {
    label: 'IP intelligence',
    items: [
      { id: 'ip-lookup', label: 'IP lookup' },
      { id: 'ip-bulk', label: 'Bulk lookup' },
      { id: 'ip-schema', label: 'IP schema' },
    ],
  },
  {
    label: 'Event search',
    items: [
      { id: 'event-search', label: 'Search' },
      { id: 'event-aggregate', label: 'Aggregate' },
      { id: 'event-search-export', label: 'Export search' },
      { id: 'event-aggregate-export', label: 'Export aggregate' },
    ],
  },
  {
    label: 'Discovery',
    items: [
      { id: 'sources', label: 'Sources' },
      { id: 'field-schema', label: 'Field schema' },
    ],
  },
  {
    label: 'Datasets',
    items: [
      { id: 'enrichment-download', label: 'Enrichment download' },
    ],
  },
]

onMounted(async () => {
  await nextTick()

  const scrollContainer = document.getElementById('main-scroll-container')
  const navbar = document.getElementById('navbar-docs')

  if (scrollContainer && navbar) {
    const existingInstance = ScrollSpy.getInstance(scrollContainer)
    if (existingInstance) {
      existingInstance.dispose()
    }

    ScrollSpy.getOrCreateInstance(scrollContainer, {
      target: '#navbar-docs',
      smoothScroll: true,
    })
  }
})
</script>

<template>
  <div class="docs-page">
    <div class="docs-inner">
      <PageToolbar>
        <ToolbarHeading
          title="API Reference"
          subtitle="Query Webscout's threat intelligence programmatically"
        />
      </PageToolbar>

      <div class="docs-body">
        <!-- Content -->
        <div class="docs-content">
          <!-- Getting started -->
          <Introduction anchor="introduction" />
          <Authentication anchor="authentication" />
          <ResponseTypes anchor="response-format" />
          <FairUse anchor="rate-limits" />

          <!-- IP intelligence -->
          <IpInfo anchor="ip-lookup" />
          <IpBulk anchor="ip-bulk" />
          <IpSchema anchor="ip-schema" />

          <!-- Event search -->
          <EventSearch anchor="event-search" />
          <EventAggregate anchor="event-aggregate" />
          <ExportSearch anchor="event-search-export" />
          <ExportAggregate anchor="event-aggregate-export" />

          <!-- Discovery -->
          <Sources anchor="sources" />
          <SourcesSchema anchor="field-schema" />

          <!-- Datasets -->
          <EnrichmentDownload anchor="enrichment-download" />
        </div>

        <!-- Navigation -->
        <aside class="docs-toc">
          <nav id="navbar-docs" class="toc-nav">
            <div v-for="group in groups" :key="group.label" class="toc-group">
              <span class="toc-group-label">{{ group.label }}</span>
              <a
                v-for="item in group.items"
                :key="item.id"
                class="nav-link toc-link"
                :href="`#${item.id}`"
              >
                {{ item.label }}
              </a>
            </div>
          </nav>
        </aside>
      </div>
    </div>
  </div>
</template>

<style scoped>
.docs-page {
  background: var(--surface-page);
  min-height: 100%;
  font-family: var(--font-heading);
}

.docs-inner {
  max-width: none;
  margin: 0;
  /* Left padding stays 0 so the toolbar sits flush against the sidebar,
     matching the other pages (#main-scroll-container supplies the rest). */
  padding: 0 0 2rem 0;
}

.docs-body {
  display: grid;
  margin-top: 1rem;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 3rem;
  align-items: start;
}

/* Content column: each section is its own flat card, separated by a clean gap */
.docs-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Sticky TOC */
.docs-toc {
  position: sticky;
  top: 1rem;
  align-self: start;
}

.toc-nav {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.toc-group {
  display: flex;
  flex-direction: column;
}

.toc-group-label {
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-faint);
  margin-bottom: 0.5rem;
  padding: 0 0.75rem;
}

.toc-link {
  color: var(--text-secondary);
  font-size: var(--fs-base);
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-sm);
  text-decoration: none;
  transition: var(--transition-fast);
}

.toc-link:hover {
  color: var(--text-primary);
  background: var(--surface-overlay);
}

.toc-link.active {
  color: var(--accent-text);
  background: var(--accent-soft);
  font-weight: 500;
}

@media (max-width: 991px) {
  .docs-body {
    grid-template-columns: 1fr;
  }

  .docs-toc {
    display: none;
  }
}
</style>
